import React from "react"
import { useId } from "@reach/auto-id"
import { isEmailValid, isNotEmpty } from "utils/validation"
import { useTranslation } from "react-i18next"
import { FormFields, FormRule, FormError, FormInputValue } from "models/form"
import {
	handleInternalInputValidation,
	FormFieldFloatingLabel,
	StatesAndHelpersFloatingLabel,
} from "../formUtils"
import { Errors } from "../Errors"

export type InputMailProps = FormFields & {
	value: string
	classNameInput?: string
	mailToConfirm?: string
}

export const InputMail: React.FC<InputMailProps> = React.forwardRef(
	(
		{
			// Attributes
			id,
			value = "",
			name,
			label = "",
			placeholder = "",
			disabled,
			readonly = false,
			// Styles
			style,
			className = "",
			classNameLabel = "",
			classNameInput = "",
			forceFloating = false,
			// Error management
			mailToConfirm = null,
			required = false,
			customRules,
			showErrors,
			// Handler
			onChange,
			onFocus,
			onBlur,
			onValidate,
		},
		ref: React.Ref<HTMLInputElement>
	) => {
		const INPUT_TYPE = "text"
		const prefix = useId()
		const inputId = id ?? `${prefix}-${name}`
		const errorId = `${inputId}-error`

		const [errors, setErrors] = React.useState<FormError[]>([])
		const [showPreventPasteError, setShowPreventPasteError] = React.useState<boolean>(false)

		const { t } = useTranslation()

		const rules: FormRule[] = React.useMemo(
			() => [
				{
					key: "mail-empty",
					check: (value: string) => (required ? isNotEmpty(value) : true),
					message: t("mail_error_mandatory", { ns: "form" }),
				},
				{
					key: "mail-invalid",
					check: (value: string) => (isNotEmpty(value) ? isEmailValid(value) : true),
					message: t("mail_error_invalid", { ns: "form" }),
				},
				{
					key: "mail-not-confirmed",
					check: (value: string) =>
						// On passe en minuscule pour éviter les erreurs de casse
						isNotEmpty(value) && mailToConfirm
							? mailToConfirm.toLowerCase() === value.toLowerCase()
							: true,
					message: t("mail_error_confirmation", {
						ns: "form",
					}),
				},
				...(customRules || []),
			],
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[required, mailToConfirm]
		)
		// customRules in dependencies array trigger an infinite loop,
		// because custom rules include a check property defining a new function,
		// preventing useMemo to return a valid shallow-comparison
		// or even useDeepMemo to return a valid deep-comparison

		const validate = React.useCallback(
			(currentValue: string) =>
				handleInternalInputValidation(currentValue, name, rules, setErrors, onValidate),
			[rules, name, onValidate]
		)

		// Validate on mount and on each value change
		React.useEffect(() => {
			validate(value)
			return () => {
				// On unmounted errors and validation are no longer needed
				setErrors([])
				if (onValidate) onValidate(name, [])
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [value, mailToConfirm])

		const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
			const inputValue: FormInputValue<string> = {
				target: {
					name: name,
					value: event.target.value.trim(),
				},
			}
			onChange && onChange(inputValue)
		}

		return (
			<FormFieldFloatingLabel
				inputType={INPUT_TYPE}
				inputValue={value}
				forceFloating={forceFloating}
			>
				{({
					floatingLabelClassname,
					onFloatingLabelFocus,
					onFloatingLabelBlur,
					inputAttributeToFixAutofill,
				}: StatesAndHelpersFloatingLabel) => (
					<div
						className={`c-form-field ${className} ${floatingLabelClassname}`}
						style={style}
					>
						<label
							className={`c-form-field__label c-label px-2 ${classNameLabel}`}
							htmlFor={inputId}
						>
							{label} {required && "*"}
						</label>

						<input
							className={`
							c-form-field__form
							c-input px-5
							${showErrors && errors.length > 0 ? "has-error " : " "}
							${classNameInput ? classNameInput : ""}
						`}
							id={inputId}
							ref={ref}
							name={name}
							type={INPUT_TYPE}
							placeholder={placeholder}
							value={value}
							disabled={disabled}
							readOnly={readonly}
							autoComplete={mailToConfirm !== null ? "off" : "email"}
							required={required}
							onChange={(event) => {
								!readonly && handleChange(event)
							}}
							onFocus={(event) => {
								onFloatingLabelFocus()
								onFocus && onFocus(event)
							}}
							onBlur={(event) => {
								onFloatingLabelBlur()
								onBlur && onBlur(event)
							}}
							onPaste={(event) => {
								if (mailToConfirm !== null) {
									event.preventDefault()
									setShowPreventPasteError(true)
									setTimeout(() => setShowPreventPasteError(false), 2000)
								}
							}}
							{...inputAttributeToFixAutofill}
							aria-describedby={errorId}
							aria-invalid={Boolean(errors.length)}
						/>

						{/* Error messages */}
						{showErrors && <Errors id={errorId} errors={errors} />}
						{showPreventPasteError && (
							<p aria-live="polite" className="text-xxs text-danger">
								{t("prevent_pasting", { ns: "form" })}
							</p>
						)}
					</div>
				)}
			</FormFieldFloatingLabel>
		)
	}
)
